import { Injectable } from '@angular/core';
import { DashboardCaseRowEntity } from '../../domain/entities/dashboard-case-row.entity';
import { DashboardCaseRowResponseDto } from '../dtos/dashboard-case-row-response.dto';
import { CreateCaseCommand, CreateEmployeeCaseCommand } from '../../domain/commands';
import { CaseAttachmentEntity } from '../../domain/entities/case-attachment.entity';
import { CaseFieldValueDisplayEntity } from '../../domain/entities/case-field-value-display.entity';
import { CaseFieldValueEntity } from '../../domain/entities/case-field-value.entity';
import { CaseFileUploadEntity } from '../../domain/entities/case-file-upload.entity';
import { CaseListItemEntity } from '../../domain/entities/case-list-item.entity';
import { CaseMessageEntity } from '../../domain/entities/case-message.entity';
import { CasePrioritySnapshotEntity } from '../../domain/entities/case-priority-snapshot.entity';
import { CaseStatusSnapshotEntity } from '../../domain/entities/case-status-snapshot.entity';
import { CaseEntity } from '../../domain/entities/case.entity';
import { ChecklistStepEntity } from '../../domain/entities/checklist-step.entity';
import { ClosingReasonEntity } from '../../domain/entities/closing-reason.entity';
import { ReopeningReasonEntity } from '../../domain/entities/reopening-reason.entity';
import { EmployeeEntity } from '../../domain/entities/employee.entity';
import { LinkedCaseEntity } from '../../domain/entities/linked-case.entity';
import { ParentChildCasesEntity } from '../../domain/entities/parent-child-cases.entity';
import { WorkNoteEntity } from '../../domain/entities/work-note.entity';
import { CaseAttachmentResponseDto } from '../dtos/case-attachment-response.dto';
import { CaseListItemResponseDto } from '../dtos/case-list-item-response.dto';
import { CaseMessageResponseDto } from '../dtos/case-message-response.dto';
import { CasePrioritySnapshotResponseDto } from '../dtos/case-priority-snapshot-response.dto';
import { CaseResponseDto } from '../dtos/case-response.dto';
import { CaseStatusSnapshotResponseDto } from '../dtos/case-status-snapshot-response.dto';
import { ChecklistStepResponseDto } from '../dtos/checklist-step-response.dto';
import { CloseCaseRequestDto } from '../dtos/close-case-request.dto';
import { ClosingReasonResponseDto } from '../dtos/closing-reason-response.dto';
import { ReopenCaseRequestDto } from '../dtos/reopen-case-request.dto';
import { ReopeningReasonResponseDto } from '../dtos/reopening-reason-response.dto';
import { CreateCaseAttachmentDto } from '../dtos/create-case-attachment.dto';
import { CreateCaseFieldValueDto } from '../dtos/create-case-field-value.dto';
import { CreateCaseFileUploadDto } from '../dtos/create-case-file-upload.dto';
import { CreateCaseRequestDto } from '../dtos/create-case-request.dto';
import { CreateEmployeeCaseRequestDto } from '../dtos/create-employee-case-request.dto';
import { EmployeeResponseDto } from '../dtos/employee-response.dto';
import { CaseAssigneeCandidateResponseDto } from '../dtos/case-assignee-candidates-response.dto';
import { LinkedCaseResponseDto } from '../dtos/linked-case-response.dto';
import { CaseParentChildResponseDto } from '../dtos/case-parent-child-response.dto';
import { UpdateCaseRequestDto } from '../dtos/update-case-request.dto';
import { WorkNoteResponseDto } from '../dtos/work-note-response.dto';
import { CasePrintResponseDto } from '../dtos/case-print-response.dto';

/**
 * Mapper for transforming Case DTOs to domain entities and vice versa.
 */
@Injectable({ providedIn: 'root' })
export class CasesMapper {
  /**
   * Maps a Case DTO to a domain entity.
   * @param dto - Case response DTO
   * @returns Case domain entity
   */
  public toDomain(dto: CaseResponseDto): CaseEntity {
    const entity = new CaseEntity();
    entity.id = dto.id; // API returns 'id', not 'caseId'
    entity.caseNumber = dto.caseNumber;
    entity.caseTypeId = dto.caseTypeId;
    entity.caseTypeName = dto.caseTypeName;
    entity.caseTypeCategoryName = dto.caseTypeCategoryName;
    entity.caseFormVersionId = dto.caseFormVersionId;
    entity.requestorUserId = dto.requestorUserId;
    entity.requestedByUserId = dto.requestedByUserId;
    entity.requestedByName = dto.requestedByName;
    entity.requestedByEmail = dto.requestedByEmail;
    entity.requestedByPhone = dto.requestedByPhone;
    entity.requestedByDivision = dto.requestedByDivision ?? null;
    entity.requestedByDepartment = dto.requestedByDepartment ?? null;
    entity.priorityCode = dto.priorityCode;
    entity.priorityName = dto.priorityName;
    entity.primaryAssigneeUserId = dto.primaryAssigneeUserId;
    entity.primaryAssigneeName = dto.primaryAssigneeName ?? null;
    entity.secondaryAssigneeUserId = dto.secondaryAssigneeUserId;
    entity.secondaryAssigneeName = dto.secondaryAssigneeName ?? null;

    // Always ensure arrays are initialized (never null/undefined)
    entity.involvedEmployeeIds = dto.involvedEmployeeIds ?? [];
    entity.fieldValues = this._mapFieldValuesToDomain(dto.fieldValues ?? []);
    entity.fileUploads = this._mapFileUploadsToDomain(dto.fileUploads ?? []);
    entity.attachments = this._mapAttachmentsToDomain(dto.attachments ?? []);

    entity.statusCategoryId = dto.statusCategoryId;
    entity.statusCategoryName = dto.statusCategoryName;
    entity.statusColor = dto.statusColor;
    entity.statusDisplayName = dto.statusDisplayName;
    entity.statusId = dto.statusId;
    entity.statusName = dto.statusName;
    entity.dueDate = dto.dueDate;
    entity.createdByUserName = dto.createdByUserName;
    entity.createdOn = dto.createdOn;
    entity.updatedOn = dto.updatedOn;

    // Closing fields (only populated when case is closed)
    entity.closingReasonId = dto.closingReasonId;
    entity.closingReasonName = dto.closingReasonName;
    entity.closingNotes = dto.closingNotes;
    entity.closedOn = dto.closedOn;
    entity.closedByUserId = dto.closedByUserId;
    entity.closedByUserName = dto.closedByUserName;

    entity.isReadOnly = dto.isReadOnly;
    entity.workflowSubStatus = dto.workflowSubStatus;
    entity.currentWorkflowStepName = dto.currentWorkflowStepName;
    entity.currentWorkflowStepType = dto.currentWorkflowStepType;
    entity.currentWorkflowHandoffTeamName = dto.currentWorkflowHandoffTeamName;
    entity.currentWorkflowHandoffTargetUserName = dto.currentWorkflowHandoffTargetUserName;
    entity.currentWorkflowHandoffDistributionMethod = dto.currentWorkflowHandoffDistributionMethod;

    return entity;
  }

  /**
   * Maps an array of Case DTOs to domain entities.
   * @param dtos - Array of Case response DTOs
   * @returns Array of Case domain entities
   */
  public toDomainArray(dtos: CaseResponseDto[]): CaseEntity[] {
    return dtos.map((dto) => this.toDomain(dto));
  }

  /**
   * Maps CreateCaseCommand to create request DTO.
   * @param command - Create case command
   * @returns Create Case request DTO
   */
  public toCreateDto(command: CreateCaseCommand): CreateCaseRequestDto {
    return {
      caseTypeId: command.caseTypeId,
      caseFormVersionId: command.caseFormVersionId,
      requestedByUserId: command.requestedByUserId,
      priorityCode: command.priorityCode,
      primaryAssigneeUserId: command.primaryAssigneeUserId,
      secondaryAssigneeUserId: command.secondaryAssigneeUserId,
      involvedEmployeeIds: command.involvedEmployeeIds,
      fieldValues: this._mapFieldValues(command.fieldValues),
      fileUploads: this._mapFileUploads(command.fileUploads),
      attachments: this._mapAttachments(command.attachments),
    };
  }

  /**
   * Maps Create Employee Case command to employee case request DTO.
   * Used for employee portal case creation endpoint.
   * Note: Does NOT include requestedByUserId or primaryAssigneeUserId - backend sets these from auth context.
   * @param command - Create employee case command
   * @returns Employee create case request DTO
   */
  public toCreateEmployeeDto(command: CreateEmployeeCaseCommand): CreateEmployeeCaseRequestDto {
    return {
      caseTypeId: command.caseTypeId,
      caseFormVersionId: command.caseFormVersionId,
      priorityCode: command.priorityCode,
      secondaryAssigneeUserId: command.secondaryAssigneeUserId,
      involvedEmployeeIds: command.involvedEmployeeIds,
      requestedByUserId: command.requestedByUserId ?? null,
      fieldValues: this._mapFieldValues(command.fieldValues),
      fileUploads: this._mapFileUploads(command.fileUploads),
      attachments: this._mapAttachments(command.attachments),
    };
  }

  /**
   * Maps Case entity data to update request DTO.
   * @param data - Partial Case entity
   * @returns Update Case request DTO
   */
  public toUpdateDto(data: Partial<CaseEntity>): UpdateCaseRequestDto {
    return {
      caseTypeId: data.caseTypeId,
      caseFormVersionId: data.caseFormVersionId,
      requestorUserId: data.requestorUserId,
      priorityCode: data.priorityCode,
      primaryAssigneeUserId: data.primaryAssigneeUserId,
      secondaryAssigneeUserId: data.secondaryAssigneeUserId,
      involvedEmployeeIds: data.involvedEmployeeIds,
      fieldValues: data.fieldValues ? this._mapFieldValues(data.fieldValues) : undefined,
      fileUploads: data.fileUploads ? this._mapFileUploads(data.fileUploads) : undefined,
      attachments: data.attachments ? this._mapAttachments(data.attachments) : undefined,
      caseId: data.id,
      caseNumber: data.caseNumber,
    };
  }

  private _mapFieldValues(fieldValues: CaseFieldValueEntity[]): CreateCaseFieldValueDto[] {
    return fieldValues.map((fv) => ({
      versionFieldId: fv.versionFieldId,
      value: fv.value,
    }));
  }

  private _mapFileUploads(fileUploads: CaseFileUploadEntity[]): CreateCaseFileUploadDto[] {
    return fileUploads.map((fu) => ({
      versionFieldId: fu.versionFieldId,
      fileName: fu.fileName,
      filePath: fu.filePath,
      fileSizeBytes: fu.fileSizeBytes,
      contentType: fu.contentType,
    }));
  }

  private _mapAttachments(attachments: CaseAttachmentEntity[]): CreateCaseAttachmentDto[] {
    return attachments.map((a) => ({
      id: a.id,
      fileName: a.fileName,
      filePath: a.filePath,
      fileSizeBytes: a.fileSizeBytes,
      contentType: a.contentType,
    }));
  }

  /**
   * Maps DTO field values to domain entities
   * Ensures proper type conversion and handles null/undefined
   */
  private _mapFieldValuesToDomain(dtos: CreateCaseFieldValueDto[]): CaseFieldValueEntity[] {
    if (!Array.isArray(dtos)) {
      return [];
    }

    return dtos.map((dto) => {
      const entity = new CaseFieldValueEntity();
      entity.versionFieldId = dto.versionFieldId;
      entity.value = dto.value;
      return entity;
    });
  }

  /**
   * Maps DTO file uploads to domain entities
   */
  private _mapFileUploadsToDomain(dtos: CreateCaseFileUploadDto[]): CaseFileUploadEntity[] {
    if (!Array.isArray(dtos)) {
      return [];
    }

    return dtos.map((dto) => {
      const entity = new CaseFileUploadEntity();
      entity.versionFieldId = dto.versionFieldId;
      entity.fileName = dto.fileName;
      entity.filePath = dto.filePath;
      entity.fileSizeBytes = dto.fileSizeBytes;
      entity.contentType = dto.contentType;
      return entity;
    });
  }

  /**
   * Maps DTO attachments to domain entities
   */
  private _mapAttachmentsToDomain(dtos: CreateCaseAttachmentDto[]): CaseAttachmentEntity[] {
    if (!Array.isArray(dtos)) {
      return [];
    }

    return dtos.map((dto) => {
      const entity = new CaseAttachmentEntity();
      entity.id = dto.id;
      entity.fileName = dto.fileName;
      entity.filePath = dto.filePath;
      entity.fileSizeBytes = dto.fileSizeBytes;
      entity.contentType = dto.contentType;
      return entity;
    });
  }

  /**
   * Maps status snapshot DTO to domain entity.
   */
  public toStatusSnapshotDomain(dto: CaseStatusSnapshotResponseDto): CaseStatusSnapshotEntity {
    const entity = new CaseStatusSnapshotEntity();
    entity.id = dto.id;
    entity.categoryId = dto.categoryId;
    entity.categoryName = dto.categoryName;
    entity.color = dto.color;
    entity.displayName = dto.displayName;
    entity.isActive = dto.isActive;
    entity.name = dto.name;
    entity.sortOrder = dto.sortOrder;
    entity.sourceStatusId = dto.sourceStatusId;
    return entity;
  }

  /**
   * Maps array of status snapshot DTOs to domain entities.
   */
  public toStatusSnapshotsDomain(
    dtos: CaseStatusSnapshotResponseDto[],
  ): CaseStatusSnapshotEntity[] {
    return dtos.map((dto) => this.toStatusSnapshotDomain(dto));
  }

  /**
   * Maps priority snapshot DTO to domain entity.
   */
  public toPrioritySnapshotDomain(
    dto: CasePrioritySnapshotResponseDto,
  ): CasePrioritySnapshotEntity {
    const entity = new CasePrioritySnapshotEntity();
    entity.id = dto.id;
    entity.priority = dto.priority;
    entity.name = dto.name;
    const normalizedName = dto.name.replace(/\s+Priority$/i, '');
    entity.code = this._toSlug(normalizedName);
    entity.sortOrder = dto.sortOrder;
    entity.timeToResolveHours = dto.timeToResolveHours;
    entity.atRiskThresholdPercent = dto.atRiskThresholdPercent;
    entity.isActive = dto.isActive;
    return entity;
  }

  /**
   * Converts a display name to its slug form used by the backend for lookup.
   * Example: "In Progress" -> "in_progress".
   */
  private _toSlug(name: string): string {
    return name.trim().toLowerCase().replace(/ /g, '_');
  }

  /**
   * Maps array of priority snapshot DTOs to domain entities.
   */
  public toPrioritySnapshotsDomain(
    dtos: CasePrioritySnapshotResponseDto[],
  ): CasePrioritySnapshotEntity[] {
    return dtos.map((dto) => this.toPrioritySnapshotDomain(dto));
  }

  /**
   * Maps checklist step DTO to domain entity.
   * API returns simplified format: { id, description, displayOrder, isCompleted }
   */
  public toChecklistStepDomain(dto: ChecklistStepResponseDto): ChecklistStepEntity {
    const entity = new ChecklistStepEntity();
    entity.id = dto.id;
    entity.stepDescription = dto.description;
    entity.sortOrder = dto.displayOrder;
    entity.isCompleted = dto.isCompleted;
    // Set defaults for fields not provided by API
    entity.caseId = '';
    entity.checklistId = '';
    entity.checklistName = '';
    entity.stepName = '';
    entity.completedByUserId = null;
    entity.completedByUserName = null;
    entity.completedOn = null;
    return entity;
  }

  /**
   * Maps array of checklist step DTOs to domain entities.
   */
  public toChecklistStepsDomain(dtos: ChecklistStepResponseDto[]): ChecklistStepEntity[] {
    return dtos.map((dto) => this.toChecklistStepDomain(dto));
  }

  /**
   * Maps message DTO to domain entity.
   */
  public toMessageDomain(dto: CaseMessageResponseDto): CaseMessageEntity {
    const entity = new CaseMessageEntity();
    entity.id = dto.id;
    entity.messageType = dto.messageType;
    entity.body = dto.body;
    entity.senderUserId = dto.senderUserId;
    entity.senderName = dto.senderName;
    entity.senderFirstName = dto.senderFirstName;
    entity.senderLastName = dto.senderLastName;
    entity.isRequestor = dto.isRequestor;
    entity.createdOn = dto.createdOn;
    entity.attachments = this.toAttachmentsDomain(dto.attachments ?? []);
    return entity;
  }

  /**
   * Maps array of message DTOs to domain entities.
   */
  public toMessagesDomain(dtos: CaseMessageResponseDto[]): CaseMessageEntity[] {
    return dtos.map((dto) => this.toMessageDomain(dto));
  }

  /**
   * Maps work note DTO to domain entity.
   */
  public toWorkNoteDomain(dto: WorkNoteResponseDto): WorkNoteEntity {
    const entity = new WorkNoteEntity();
    entity.id = dto.id;
    entity.body = dto.body;
    entity.senderUserId = dto.senderUserId;
    entity.senderName = dto.senderName;
    entity.senderFirstName = dto.senderFirstName;
    entity.senderLastName = dto.senderLastName;
    entity.noteType = dto.noteType;
    entity.createdOn = dto.createdOn;
    entity.attachments = this.toAttachmentsDomain(dto.attachments ?? []);
    return entity;
  }

  /**
   * Maps array of work note DTOs to domain entities.
   */
  public toWorkNotesDomain(dtos: WorkNoteResponseDto[]): WorkNoteEntity[] {
    return dtos.map((dto) => this.toWorkNoteDomain(dto));
  }

  /**
   * Maps attachment DTO to domain entity.
   */
  /**
   * Maps attachment DTO to domain entity.
   * Backend always returns all fields (camelCase JSON from PascalCase C# properties).
   */
  public toAttachmentDomain(dto: CaseAttachmentResponseDto): CaseAttachmentEntity {
    const entity = new CaseAttachmentEntity();
    entity.id = dto.id;
    entity.fileName = dto.fileName;
    entity.filePath = dto.filePath;
    entity.fileSizeBytes = dto.fileSizeBytes;
    entity.contentType = dto.contentType;
    entity.uploadedByUserId = dto.uploadedByUserId;
    entity.uploadedByUserName = dto.uploadedByUserName;
    entity.uploadedOn = new Date(dto.createdOn);

    return entity;
  }

  /**
   * Maps array of attachment DTOs to domain entities.
   */
  public toAttachmentsDomain(dtos: CaseAttachmentResponseDto[]): CaseAttachmentEntity[] {
    return dtos.map((dto) => this.toAttachmentDomain(dto));
  }

  /**
   * Maps linked case DTO to domain entity.
   * NOTE: API now returns camelCase (changed from snake_case)
   */
  public toLinkedCaseDomain(dto: LinkedCaseResponseDto): LinkedCaseEntity {
    const entity = new LinkedCaseEntity();
    entity.id = dto.id;
    entity.caseId = dto.caseId;
    entity.linkedCaseId = dto.linkedCaseId;
    entity.linkedCaseNumber = dto.linkedCaseNumber;
    entity.linkedCaseTypeName = dto.linkedCaseTypeName;
    entity.linkedCaseTypeCategoryName = dto.linkedCaseTypeCategoryName;
    entity.linkedCaseStatusName = dto.linkedCaseStatusName;
    entity.linkedCaseStatusColor = dto.linkedCaseStatusColor;
    entity.linkedCaseRequestorName = dto.linkedCaseRequestorName;
    entity.linkedCaseCreatedOn = dto.linkedCaseCreatedOn;
    entity.linkType = dto.linkType;
    entity.linkedOn = dto.linkedOn;
    entity.linkedByUserId = dto.linkedByUserId;
    entity.linkedByUserName = dto.linkedByUserName;
    entity.isAutoLinked = dto.isAutoLinked ?? false;
    return entity;
  }

  /**
   * Maps array of linked case DTOs to domain entities.
   */
  public toLinkedCasesDomain(dtos: LinkedCaseResponseDto[]): LinkedCaseEntity[] {
    return dtos.map((dto) => this.toLinkedCaseDomain(dto));
  }

  /**
   * Maps parent/child cases DTO to domain entity.
   */
  public toParentChildCasesDomain(dto: CaseParentChildResponseDto): ParentChildCasesEntity {
    const entity = new ParentChildCasesEntity();
    entity.parent = dto.parent ? this.toLinkedCaseDomain(dto.parent) : null;
    entity.children = (dto.children ?? []).map((c) => this.toLinkedCaseDomain(c));
    return entity;
  }

  public toEmployeeDomain(dto: EmployeeResponseDto): EmployeeEntity {
    const entity = new EmployeeEntity();
    entity.userId = dto.userId;
    entity.firstName = dto.firstName;
    entity.lastName = dto.lastName;
    entity.email = dto.email;
    entity.phone = dto.phone;
    entity.isActive = dto.isActive;
    return entity;
  }

  /**
   * Maps array of employee DTOs to domain entities.
   */
  public toEmployeesDomain(dtos: EmployeeResponseDto[]): EmployeeEntity[] {
    return dtos.map((dto) => this.toEmployeeDomain(dto));
  }

  public watcherOptionToEmployeeDomain(dto: CaseAssigneeCandidateResponseDto): EmployeeEntity {
    const entity = new EmployeeEntity();
    entity.userId = dto.userId;
    entity.firstName = dto.firstName;
    entity.lastName = dto.lastName;
    entity.email = dto.email;
    entity.phone = '';
    entity.isActive = true;
    return entity;
  }

  public watcherOptionsToEmployeesDomain(dtos: CaseAssigneeCandidateResponseDto[]): EmployeeEntity[] {
    return dtos.map((dto) => this.watcherOptionToEmployeeDomain(dto));
  }

  /**
   * Maps a ClosingReasonResponseDto to ClosingReasonEntity.
   * @param dto - Closing reason response DTO
   * @returns Closing reason domain entity
   */
  public toClosingReasonEntity(dto: ClosingReasonResponseDto): ClosingReasonEntity {
    return new ClosingReasonEntity({
      id: dto.id,
      name: dto.name,
      sortOrder: dto.sortOrder,
      isActive: true, // Backend only returns active reasons
    });
  }

  /**
   * Maps array of ClosingReasonResponseDto to domain entities.
   * @param dtos - Array of closing reason response DTOs
   * @returns Array of closing reason domain entities
   */
  public toClosingReasonEntities(dtos: ClosingReasonResponseDto[]): ClosingReasonEntity[] {
    return dtos.map((dto) => this.toClosingReasonEntity(dto));
  }

  /**
   * Maps CloseCaseCommand to CloseCaseRequestDto.
   * @param command - Close case command with statusId, closingReasonId, closingNotes
   * @returns Close case request DTO for API
   */
  public toCloseCaseRequestDto(command: {
    statusId: string;
    closingReasonId: string;
    closingNotes: string;
  }): CloseCaseRequestDto {
    return {
      statusId: command.statusId,
      closingReasonId: command.closingReasonId,
      closingNotes: command.closingNotes,
    };
  }

  /**
   * Maps a ReopeningReasonResponseDto to ReopeningReasonEntity.
   * @param dto - Reopening reason response DTO
   * @returns Reopening reason domain entity
   */
  public toReopeningReasonEntity(dto: ReopeningReasonResponseDto): ReopeningReasonEntity {
    return new ReopeningReasonEntity({
      id: dto.id,
      name: dto.name,
      sortOrder: dto.sortOrder,
      isActive: true, // Backend only returns active reasons
    });
  }

  /**
   * Maps array of ReopeningReasonResponseDto to domain entities.
   * @param dtos - Array of reopening reason response DTOs
   * @returns Array of reopening reason domain entities
   */
  public toReopeningReasonEntities(dtos: ReopeningReasonResponseDto[]): ReopeningReasonEntity[] {
    return dtos.map((dto) => this.toReopeningReasonEntity(dto));
  }

  /**
   * Maps ReopenCaseCommand to ReopenCaseRequestDto.
   * @param command - Reopen case command with reopeningReasonId, reopeningNotes
   * @returns Reopen case request DTO for API
   */
  public toReopenCaseRequestDto(command: {
    reopeningReasonId: string;
    reopeningNotes: string;
  }): ReopenCaseRequestDto {
    return {
      reopeningReasonId: command.reopeningReasonId,
      reopeningNotes: command.reopeningNotes,
    };
  }

  /**
   * Maps case list item DTO to domain entity.
   * API returns camelCase, entity uses camelCase (no conversion needed).
   * @param dto - Case list item response DTO
   * @returns Case list item domain entity
   */
  public toCaseListItemDomain(dto: CaseListItemResponseDto): CaseListItemEntity {
    const entity = new CaseListItemEntity();
    entity.id = dto.id;
    entity.caseNumber = dto.caseNumber;
    entity.caseTypeName = dto.caseTypeName;
    entity.caseTypeCategoryName = dto.caseTypeCategoryName;
    entity.requestorName = dto.requestorName;
    entity.statusName = dto.statusName;
    entity.statusDisplayName = dto.statusDisplayName;
    entity.statusColor = dto.statusColor;
    entity.statusCategoryName = dto.statusCategoryName ?? '';
    entity.priorityCode = dto.priorityCode;
    entity.primaryAssigneeName = dto.primaryAssigneeName;
    entity.createdOn = dto.createdOn;
    entity.dueDate = dto.dueDate;
    entity.unreadMessageCount = dto.unreadMessageCount;
    return entity;
  }

  /**
   * Maps array of case list item DTOs to domain entities.
   * @param dtos - Array of case list item response DTOs
   * @returns Array of case list item domain entities
   */
  public toCaseListItemsDomain(dtos: CaseListItemResponseDto[]): CaseListItemEntity[] {
    return dtos.map((dto) => this.toCaseListItemDomain(dto));
  }

  /**
   * Maps case print response DTO to a tuple of domain entities.
   * Returns case entity, display field values, and involved employees.
   * @param dto - Case print response DTO
   * @returns Tuple of [CaseEntity, CaseFieldValueDisplayEntity[], EmployeeEntity[]]
   */
  public toPrintDomain(dto: CasePrintResponseDto): {
    case: CaseEntity;
    fieldValues: CaseFieldValueDisplayEntity[];
    involvedEmployees: EmployeeEntity[];
  } {
    // Map case detail to CaseEntity
    const caseDto = dto.caseDetail;
    const caseEntity = new CaseEntity();
    caseEntity.id = caseDto.id;
    caseEntity.caseNumber = caseDto.caseNumber;
    caseEntity.caseTypeId = caseDto.caseTypeId;
    caseEntity.caseTypeName = caseDto.caseTypeName;
    caseEntity.caseTypeCategoryName = caseDto.caseTypeCategoryName;
    caseEntity.caseFormVersionId = caseDto.caseFormVersionId;
    caseEntity.requestedByUserId = caseDto.requestedByUserId;
    caseEntity.requestedByName = caseDto.requestedByName;
    caseEntity.requestedByEmail = caseDto.requestedByEmail;
    caseEntity.requestedByPhone = caseDto.requestedByPhone ?? '';
    caseEntity.requestedByDivision = caseDto.requestedByDivision ?? null;
    caseEntity.requestedByDepartment = caseDto.requestedByDepartment ?? null;
    caseEntity.priorityCode = caseDto.priorityCode;
    caseEntity.priorityName = caseDto.priorityName;
    caseEntity.primaryAssigneeUserId = caseDto.primaryAssigneeUserId;
    caseEntity.primaryAssigneeName = caseDto.primaryAssigneeName ?? null;
    caseEntity.secondaryAssigneeUserId = caseDto.secondaryAssigneeUserId;
    caseEntity.secondaryAssigneeName = caseDto.secondaryAssigneeName ?? null;
    caseEntity.statusCategoryId = caseDto.statusCategoryId;
    caseEntity.statusCategoryName = caseDto.statusCategoryName;
    caseEntity.statusColor = caseDto.statusColor;
    caseEntity.statusDisplayName = caseDto.statusDisplayName;
    caseEntity.statusId = caseDto.statusId;
    caseEntity.statusName = caseDto.statusName;
    caseEntity.dueDate = caseDto.dueDate;
    caseEntity.createdByUserName = caseDto.createdByUserName;
    caseEntity.createdOn = caseDto.createdOn;
    caseEntity.updatedOn = caseDto.updatedOn;
    caseEntity.closingReasonId = caseDto.closingReasonId;
    caseEntity.closingReasonName = caseDto.closingReasonName;
    caseEntity.closingNotes = caseDto.closingNotes;
    caseEntity.closedOn = caseDto.closedOn;
    caseEntity.closedByUserId = caseDto.closedByUserId;
    caseEntity.closedByUserName = caseDto.closedByUserName;

    // Initialize arrays
    caseEntity.involvedEmployeeIds = [];
    caseEntity.fieldValues = [];
    caseEntity.fileUploads = [];
    caseEntity.attachments = [];

    // Map field values to CaseFieldValueDisplayEntity
    const fieldValues = dto.fieldValues.map((fv) => {
      const entity = new CaseFieldValueDisplayEntity();
      entity.id = fv.id;
      entity.versionFieldId = fv.versionFieldId;
      entity.fieldLabel = fv.fieldLabel;
      entity.fieldType = fv.fieldType;
      entity.value = fv.value;
      entity.isRequired = fv.isRequired;
      entity.isVisibleToRequester = fv.isVisibleToRequester;
      entity.sortOrder = fv.sortOrder;
      return entity;
    });

    // Map involved employees to EmployeeEntity
    const involvedEmployees = dto.involvedEmployees.map((emp) => {
      const entity = new EmployeeEntity();
      entity.userId = emp.userId;
      entity.firstName = emp.firstName;
      entity.lastName = emp.lastName;
      entity.email = emp.email;
      entity.phone = emp.phone;
      entity.isActive = true; // Involved employees are always active in print context
      return entity;
    });

    return {
      case: caseEntity,
      fieldValues,
      involvedEmployees,
    };
  }

  public toDashboardCaseRowDomain(dto: DashboardCaseRowResponseDto): DashboardCaseRowEntity {
    const entity = new DashboardCaseRowEntity();
    entity.id = dto.id; entity.caseNumber = dto.caseNumber; entity.caseTypeName = dto.caseTypeName;
    entity.caseTypeCategoryName = dto.caseTypeCategoryName; entity.requestorName = dto.requestorName;
    entity.statusName = dto.statusName; entity.statusDisplayName = dto.statusDisplayName;
    entity.statusColor = dto.statusColor; entity.statusCategoryId = dto.statusCategoryId; entity.statusCategoryName = dto.statusCategoryName ?? null; entity.isReadOnly = dto.isReadOnly;
    entity.priorityCode = dto.priorityCode; entity.primaryAssigneeUserId = dto.primaryAssigneeUserId;
    entity.primaryAssigneeName = dto.primaryAssigneeName; entity.secondaryAssigneeUserId = dto.secondaryAssigneeUserId;
    entity.secondaryAssigneeName = dto.secondaryAssigneeName; entity.assignedTeamId = dto.assignedTeamId;
    entity.assignedTeamName = dto.assignedTeamName; entity.requestorEmail = dto.requestorEmail;
    entity.requestorDepartment = dto.requestorDepartment; entity.requestorDivision = dto.requestorBusinessUnit;
    entity.createdByUserId = dto.createdByUserId; entity.createdByName = dto.createdByName;
    entity.updatedByUserId = dto.updatedByUserId; entity.updatedByName = dto.updatedByName;
    entity.createdOn = dto.createdOn; entity.updatedOn = dto.updatedOn;
    entity.dueDate = dto.dueDate; entity.slaStatus = dto.slaStatus;
    entity.unreadMessageCount = dto.unreadMessageCount;
    entity.hasMessages = dto.hasMessages;
    entity.isCurrentUserWatching = dto.isCurrentUserWatching;
    entity.requestedByUserId = dto.requestedByUserId;
    entity.excludedAssigneeUserIds = dto.excludedAssigneeUserIds ?? [];
    return entity;
  }

  public toDashboardCaseRowsDomain(dtos: DashboardCaseRowResponseDto[]): DashboardCaseRowEntity[] {
    return dtos.map((dto) => this.toDashboardCaseRowDomain(dto));
  }
}
